import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spinner,
  Text,
  useOutsideClick,
  useToast,
} from "@chakra-ui/react";
import XRPLogo from "../assets/imgs/XRP.svg";
import ThetherLogo from "../assets/imgs/thether.svg";
import RlusdIcon from "../assets/imgs/rlusd-icon.png";
import Logo from "../assets/imgs/sm-logo.png";
import ObjectScanSvg from "../assets/imgs/object-scan.svg";
import {
  IconArrowsUpDown,
  IconChevronDown,
  IconChevronLeft,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QRCode } from "react-qrcode-logo";
import { io, Socket } from "socket.io-client";
import { apiFetch } from "../utils/apiClient";
import {
  clearSwapSession,
  readSwapSession,
  writeSwapSession,
} from "../utils/swapSessionStorage";
import {
  clearSwapPrefill,
  readSwapPrefill,
  writeSwapPrefill,
} from "../utils/swapPrefillStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || API_BASE_URL;

/** iOS Safari often stalls WebSockets while the wallet app is foregrounded; polling-first can be more reliable. */
function isIOSBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

function createClientSwapId(): string {
  const c =
    typeof globalThis !== "undefined"
      ? (globalThis as { crypto?: Crypto }).crypto
      : undefined;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  if (c && typeof c.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type QrCodeResponse = {
  channel_data_hash: string;
};

type SwapCoin = {
  id?: string;
  symbol?: string;
  name?: string;
  isActive?: boolean;
  swapPairs?: string[];
};

type BalanceItem = {
  coin?: SwapCoin;
  balance?: number | string;
};

type BalancesResponse = {
  balances?: BalanceItem[];
};

type CoinPricesResponse =
  | Record<string, unknown>
  | Array<Record<string, unknown>>;

type WalletLoginPayload = {
  status?: string;
  token?: string;
  user?: {
    id?: string | number;
    wallet_address?: string;
    name?: string;
    balances?: unknown;
    channel_data_hash?: string;
  };
};

type WalletForegroundHintVariant = "swap" | "connection";

type SwapCardProps = {
  connectRequestNonce?: number;
  isConnected?: boolean;
  onWalletConnected?: (walletAddress?: string) => void;
};

const ALLOWED_FROM_SYMBOLS = new Set(["XRP", "RLUSD"]);
const ALLOWED_TO_SYMBOL = "XRPHAI";
const XRPHAI_REWARDS_URL = "https://xrphtoken.com/xrphai-rewards";
const XRPH_WALLET_APP_URL = "https://xrphtoken.com/app";
const XRPH_WALLET_IOS_PAYMENT_URL_BASE = "https://xrphwallet.web.app/payment";
const QUERY_WALLET_PARAM = "wallet";
const QUERY_FROM_SYMBOL_PARAM = "from";
const QUERY_TO_SYMBOL_PARAM = "to";
const QUERY_FROM_AMOUNT_PARAM = "fromAmount";

/**
 * Swap flow only: bring XRPH Wallet to the foreground without `payment` / `paymentrequest`
 * URLs (those routes are for connect / “Open in XRPH Wallet” and can show the connect modal).
 */
function openXrphWalletAppForegroundFromSwapMobileBrowser(): void {
  const ua = navigator.userAgent || "";
  if (!/Android|iPhone|iPad|iPod/i.test(ua)) {
    return;
  }
  if (/Android/i.test(ua)) {
    window.location.assign(
      "intent://#Intent;scheme=com.xrphealthcare.xrphwallet;package=com.xrphwallet;end",
    );
    return;
  }
  // iOS: `xrphtoken.com/app` is not (reliably) a Universal Link for the wallet, so Safari stays
  // in-browser. Use the app’s URL scheme — same scheme as in the Android intent above.
  if (/iPhone|iPad|iPod/i.test(ua)) {
    window.location.assign("com.xrphealthcare.xrphwallet://");
    return;
  }

  window.location.assign(XRPH_WALLET_APP_URL);
}

/** After app handoff; if redirect worked, user often leaves before this fires. */
const SWAP_MOBILE_MANUAL_HINT_MODAL_DELAY_MS = 1200;
const SWAP_MOBILE_APP_OPEN_DELAY_MS = 120;

const balanceNumber = (row?: BalanceItem) => {
  const raw = row?.balance;
  if (raw === undefined || raw === null) return 0;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const tokenImage = (symbol: string) => {
  const upper = symbol.toUpperCase();
  if (upper === "USDT") return ThetherLogo;
  if (upper === "RLUSD") return RlusdIcon;
  if (upper === "XRPH" || upper === "XRPHAI") return Logo;
  return XRPLogo;
};

/** sm-logo.png is high-res; without boxSize, Chakra Image blows up the row. */
const TOKEN_ICON_BOX: {
  boxSize: string;
  objectFit: "contain";
  flexShrink: number;
} = {
  boxSize: "28px",
  objectFit: "contain",
  flexShrink: 0,
};

function SwapCard({
  connectRequestNonce = 0,
  isConnected: externalIsConnected,
  onWalletConnected,
}: SwapCardProps) {
  const dropRef = useRef(null);
  const receiveDropRef = useRef(null);
  const inputRef = useRef(null);
  const amountInputRef = useRef<HTMLInputElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  /** Tracks an in-flight swap session for recovering listeners after iOS backgrounding / reconnect. */
  const pendingSwapGateRef = useRef<{ id: string } | null>(null);
  const swapSocketHandlersRef = useRef<{
    id: string;
    onSwapResult: (payload?: {
      status?: string;
      message?: string;
      error?: string;
    }) => void | Promise<void>;
    onSwapRequestError: (error: { message?: string; error?: string }) => void;
    onSwapResponseError: (error: { message?: string; error?: string }) => void;
  } | null>(null);
  const swapInFlightRef = useRef(false);
  const swapHintShowTimerRef = useRef<number | null>(null);
  const openInWalletHintTimerRef = useRef<number | null>(null);
  const balanceBootstrapHandledRef = useRef(false);
  const didHydrateSessionRef = useRef(false);

  const [isDropdown, setIsDropdown] = useState(false);
  const [isReceiveDropdown, setIsReceiveDropdown] = useState(false);
  const [isConnect, setIsConnect] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isInputActive, setIsInputActive] = useState(false);
  /** Always pay from XRP/USDT/RLUSD; inverse swap is not supported. */
  const [isCryptoPay] = useState(true);
  const [walletAddress, setWalletAddress] = useState("");
  const [connectToken, setConnectToken] = useState("");
  const [channelDataHash, setChannelDataHash] = useState("");

  const [amount, setAmount] = useState("");
  const [prefillFromSymbol, setPrefillFromSymbol] = useState("");
  const [unconnectedPaySymbol, setUnconnectedPaySymbol] = useState("XRP");
  const [isSwapLoading, setIsSwapLoading] = useState(false);
  const [coinPrices, setCoinPrices] = useState<CoinPricesResponse | null>(null);

  const [balanceRows, setBalanceRows] = useState<BalanceItem[]>([]);
  const [selectedExternalCoinId, setSelectedExternalCoinId] = useState("");
  const [selectedReceiveCoinId, setSelectedReceiveCoinId] = useState("");
  const [successRewardsModalOpen, setSuccessRewardsModalOpen] = useState(false);
  const [connectHelpOpen, setConnectHelpOpen] = useState(false);
  const [swapOpenWalletHintOpen, setSwapOpenWalletHintOpen] = useState(false);
  const [walletForegroundHintVariant, setWalletForegroundHintVariant] =
    useState<WalletForegroundHintVariant>("swap");

  const toast = useToast();

  const cleanupSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  const clearPendingSwapHintModalTimer = () => {
    if (swapHintShowTimerRef.current !== null) {
      window.clearTimeout(swapHintShowTimerRef.current);
      swapHintShowTimerRef.current = null;
    }
  };

  const clearOpenInWalletHintModalTimer = () => {
    if (openInWalletHintTimerRef.current !== null) {
      window.clearTimeout(openInWalletHintTimerRef.current);
      openInWalletHintTimerRef.current = null;
    }
  };

  const sanitizeAmountInput = (raw: string) => {
    // Allow digits and a single dot for decimals. Strip everything else.
    let next = (raw || "").replace(/[^\d.]/g, "");
    const firstDot = next.indexOf(".");
    if (firstDot !== -1) {
      next =
        next.slice(0, firstDot + 1) + next.slice(firstDot + 1).replace(/\./g, "");
    }
    // Normalize leading dot to "0."
    if (next.startsWith(".")) next = `0${next}`;
    return next;
  };

  const reattachPendingSwapSocketSubscriptions = useCallback(() => {
    const gate = pendingSwapGateRef.current;
    const hx = swapSocketHandlersRef.current;
    const socket = socketRef.current;
    if (
      !gate ||
      !hx ||
      gate.id !== hx.id ||
      !socket ||
      !swapInFlightRef.current
    ) {
      return;
    }
    const { id, onSwapResult, onSwapRequestError, onSwapResponseError } = hx;
    if (!socket.connected) {
      socket.connect();
    }
    socket.off(`swap-request-${id}`, onSwapResult);
    socket.off(`swap-request-error-${id}`, onSwapRequestError);
    socket.off(`swap-response-error-${id}`, onSwapResponseError);
    socket.on(`swap-request-${id}`, onSwapResult);
    socket.on(`swap-request-error-${id}`, onSwapRequestError);
    socket.on(`swap-response-error-${id}`, onSwapResponseError);
  }, []);

  useEffect(() => {
    if (!SOCKET_BASE_URL) {
      return;
    }

    const socket = io(`${SOCKET_BASE_URL}/swap-live`, {
      transports: isIOSBrowser()
        ? ["polling", "websocket"]
        : ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;

    socket.on("connect", reattachPendingSwapSocketSubscriptions);

    return () => {
      socket.off("connect", reattachPendingSwapSocketSubscriptions);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [reattachPendingSwapSocketSubscriptions]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      reattachPendingSwapSocketSubscriptions();
    };
    const onPageShow = () => {
      reattachPendingSwapSocketSubscriptions();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [reattachPendingSwapSocketSubscriptions]);

  useLayoutEffect(() => {
    if (didHydrateSessionRef.current) {
      return;
    }
    didHydrateSessionRef.current = true;
    const s = readSwapSession();
    if (!s?.token || !s.channel_data_hash || !s.wallet_address) {
      return;
    }
    setConnectToken(s.token);
    setChannelDataHash(s.channel_data_hash);
    setWalletAddress(s.wallet_address);
    setIsConnected(true);
    clearOpenInWalletHintModalTimer();
    setSwapOpenWalletHintOpen(false);
    setWalletForegroundHintVariant("swap");
    onWalletConnected?.(s.wallet_address);
  }, [onWalletConnected]);

  useEffect(() => {
    if (typeof externalIsConnected === "boolean") {
      setIsConnected(externalIsConnected);
      if (!externalIsConnected) {
        clearOpenInWalletHintModalTimer();
        setSwapOpenWalletHintOpen(false);
        setWalletForegroundHintVariant("swap");
        setWalletAddress("");
        setConnectToken("");
        setChannelDataHash("");
        setBalanceRows([]);
        setAmount("");
        setPrefillFromSymbol("");
        clearSwapPrefill();
        setSelectedReceiveCoinId("");
      } else {
        clearOpenInWalletHintModalTimer();
        setSwapOpenWalletHintOpen(false);
        setWalletForegroundHintVariant("swap");
      }
    }
  }, [externalIsConnected]);

  /** Mobile app emits `disconnect-connection`; server broadcasts `disconnect-connection-<channel_data_hash>`. */
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !channelDataHash || !isConnected || !connectToken) {
      return;
    }

    const eventName = `disconnect-connection-${channelDataHash}`;
    const onDisconnectFromMobile = (payload?: { token?: string }) => {
      if (payload?.token !== connectToken) {
        return;
      }
      clearSwapSession();
      pendingSwapGateRef.current = null;
      const hx = swapSocketHandlersRef.current;
      if (hx) {
        socket.off(`swap-request-${hx.id}`, hx.onSwapResult);
        socket.off(`swap-request-error-${hx.id}`, hx.onSwapRequestError);
        socket.off(`swap-response-error-${hx.id}`, hx.onSwapResponseError);
        swapSocketHandlersRef.current = null;
      }
      socket.off(channelDataHash);
      socket.off(eventName, onDisconnectFromMobile);
      setWalletAddress("");
      setConnectToken("");
      setChannelDataHash("");
      setBalanceRows([]);
      setAmount("");
      setPrefillFromSymbol("");
      clearSwapPrefill();
      setSelectedReceiveCoinId("");
      setIsConnected(false);
      setIsConnect(false);
      setIsConnecting(false);
      swapInFlightRef.current = false;
      setIsSwapLoading(false);
      clearPendingSwapHintModalTimer();
      clearOpenInWalletHintModalTimer();
      setSwapOpenWalletHintOpen(false);
      onWalletConnected?.("");
      toast({
        status: "info",
        title: "Wallet disconnected",
        description: "This connection was removed from your mobile wallet.",
      });
    };

    socket.on(eventName, onDisconnectFromMobile);
    return () => {
      socket.off(eventName, onDisconnectFromMobile);
    };
  }, [channelDataHash, isConnected, connectToken, onWalletConnected, toast]);

  /** After refresh (hydrate) or reconnect, load balances once token + wallet exist. */
  useEffect(() => {
    if (!isConnected || !walletAddress || !connectToken) {
      balanceBootstrapHandledRef.current = false;
      return;
    }
    if (balanceBootstrapHandledRef.current) {
      return;
    }
    balanceBootstrapHandledRef.current = true;
    loadBalances(walletAddress).catch(() => {
      // no-op; card may still work for swap if balances endpoint is flaky
    });
    loadCoinPrices().catch(() => {
      // no-op
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadBalances/loadCoinPrices are stable enough for this bootstrap
  }, [isConnected, walletAddress, connectToken]);

  const xrphCoin = useMemo(
    () =>
      balanceRows.find((row) => {
        const symbol = (row.coin?.symbol || "").toUpperCase();
        return symbol === "XRPH";
      }),
    [balanceRows],
  );

  const availableExternalRows = useMemo(
    () =>
      balanceRows.filter((row) => {
        const coin = row.coin;
        const symbol = (coin?.symbol || "").toUpperCase();
        const hasId = !!coin?.id;
        const hasPairs = (coin?.swapPairs || []).length > 0;
        const isActive = coin?.isActive !== false;
        // TODO: Re-enable broader symbol list when more swap pairs are approved.
        // return symbol !== "XRPH" && hasId && hasPairs && isActive;
        return (
          ALLOWED_FROM_SYMBOLS.has(symbol) && hasId && hasPairs && isActive
        );
      }),
    [balanceRows],
  );

  const selectedExternalRow = useMemo(
    () =>
      availableExternalRows.find(
        (row) => row.coin?.id === selectedExternalCoinId,
      ) || availableExternalRows[0],
    [availableExternalRows, selectedExternalCoinId],
  );

  const clearPrefillState = () => {
    setPrefillFromSymbol("");
    setAmount("");
  };

  const parseAndPersistPrefillFromQuery = () => {
    const params = new URLSearchParams(window.location.search || "");
    const rawFromSymbol = (params.get(QUERY_FROM_SYMBOL_PARAM) || "").trim();
    const rawToSymbol = (params.get(QUERY_TO_SYMBOL_PARAM) || "").trim();
    const rawFromAmount = (params.get(QUERY_FROM_AMOUNT_PARAM) || "").trim();
    const rawWallet = (params.get(QUERY_WALLET_PARAM) || "").trim();

    const hasAnyPrefillParam = !!rawFromSymbol || !!rawFromAmount || !!rawToSymbol;
    if (!hasAnyPrefillParam) {
      // Visiting the swap page without params should not leak previous prefill.
      clearSwapPrefill();
      clearPrefillState();
      return;
    }

    const fromSymbolUpper = rawFromSymbol.toUpperCase();
    const toSymbolUpper = rawToSymbol.toUpperCase();
    const amountNum = Number(rawFromAmount);
    const isValidSymbol = ALLOWED_FROM_SYMBOLS.has(fromSymbolUpper);
    const isValidAmount =
      rawFromAmount !== "" && Number.isFinite(amountNum) && amountNum > 0;
    const isValidTo =
      !rawToSymbol || toSymbolUpper === "" || toSymbolUpper === ALLOWED_TO_SYMBOL;

    // `fromAmount` is optional. If it's missing, we still want to preselect the pay token.
    if (!isValidSymbol || !isValidTo) {
      clearSwapPrefill();
      clearPrefillState();
      return;
    }

    setPrefillFromSymbol(fromSymbolUpper);
    setUnconnectedPaySymbol(fromSymbolUpper);
    if (isValidAmount) {
      setAmount(rawFromAmount);
      // Optional UX: focus amount input after deep link hydration.
      window.setTimeout(() => {
        amountInputRef.current?.focus();
      }, 0);
      // Persist only when amount is present so storage stays meaningful.
      writeSwapPrefill({
        wallet_address: rawWallet || undefined,
        from_symbol: fromSymbolUpper,
        from_amount: rawFromAmount,
        created_at: Date.now(),
      });
    } else {
      setAmount("");
      clearSwapPrefill();
    }
  };

  useEffect(() => {
    // 1) If params exist, store and apply prefill.
    // 2) If user hits a plain link (no params), clear stored prefill immediately.
    parseAndPersistPrefillFromQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only once on mount
  }, []);

  useEffect(() => {
    // Apply prefill from storage if present (e.g. refresh) until swap completes.
    const stored = readSwapPrefill();
    if (!stored) return;

    // If prefill was bound to a wallet, do not show it for a different connected wallet.
    if (stored.wallet_address && walletAddress && stored.wallet_address !== walletAddress) {
      clearSwapPrefill();
      clearPrefillState();
      return;
    }

    // If this prefill was created without a wallet param, bind it to the first wallet that connects,
    // so another wallet won't inherit it later.
    if (!stored.wallet_address && walletAddress) {
      writeSwapPrefill({
        ...stored,
        wallet_address: walletAddress,
      });
    }

    setPrefillFromSymbol((stored.from_symbol || "").toUpperCase());
    setAmount(stored.from_amount || "");
  }, [walletAddress]);

  useEffect(() => {
    // Once balance options are present, select the requested pay token.
    if (!prefillFromSymbol || availableExternalRows.length === 0) return;
    const match = availableExternalRows.find(
      (row) => (row.coin?.symbol || "").toUpperCase() === prefillFromSymbol,
    );
    if (match?.coin?.id && match.coin.id !== selectedExternalCoinId) {
      setSelectedExternalCoinId(match.coin.id);
    }
  }, [availableExternalRows, prefillFromSymbol, selectedExternalCoinId]);

  useEffect(() => {
    // If user changes the pay token after arriving with prefill, keep storage in sync.
    if (!prefillFromSymbol) return;
    const currentSymbol = (selectedExternalRow?.coin?.symbol || "").toUpperCase();
    if (currentSymbol && currentSymbol !== prefillFromSymbol) {
      setPrefillFromSymbol(currentSymbol);
    }
  }, [prefillFromSymbol, selectedExternalRow?.coin?.symbol]);

  useEffect(() => {
    // Persist prefilled swap inputs until swap is completed.
    if (!prefillFromSymbol) return;

    const amountNum = Number(amount);
    if (amount === "") {
      clearSwapPrefill();
      setPrefillFromSymbol("");
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      return;
    }

    const existing = readSwapPrefill();
    writeSwapPrefill({
      wallet_address: existing?.wallet_address || walletAddress || undefined,
      from_symbol: prefillFromSymbol,
      from_amount: amount,
      created_at: existing?.created_at || Date.now(),
    });
  }, [amount, prefillFromSymbol, walletAddress]);

  useEffect(() => {
    if (!selectedExternalCoinId && selectedExternalRow?.coin?.id) {
      setSelectedExternalCoinId(selectedExternalRow.coin.id);
      return;
    }
    if (
      selectedExternalCoinId &&
      !availableExternalRows.some(
        (row) => row.coin?.id === selectedExternalCoinId,
      )
    ) {
      setSelectedExternalCoinId(availableExternalRows[0]?.coin?.id || "");
    }
  }, [availableExternalRows, selectedExternalCoinId, selectedExternalRow]);

  const receiveOptions = useMemo(() => {
    if (!isCryptoPay) return [];
    // TODO: Re-enable dynamic receive options from backend swapPairs when needed.
    // const pairIds = selectedExternalRow?.coin?.swapPairs || [];
    // return pairIds
    //   .map((id) => balanceRows.find((row) => row.coin?.id === id))
    //   .filter((row): row is BalanceItem => !!row?.coin?.id);
    const xrphaiRow = balanceRows.find(
      (row) => (row.coin?.symbol || "").toUpperCase() === ALLOWED_TO_SYMBOL,
    );
    return xrphaiRow ? [xrphaiRow] : [];
  }, [isCryptoPay, balanceRows]);

  const selectedReceiveRow = useMemo(() => {
    if (!isCryptoPay) return undefined;
    if (receiveOptions.length === 1) {
      return receiveOptions[0];
    }
    if (receiveOptions.length > 1 && selectedReceiveCoinId) {
      const picked = receiveOptions.find(
        (row) => row.coin?.id === selectedReceiveCoinId,
      );
      if (picked) return picked;
    }
    return receiveOptions[0];
  }, [isCryptoPay, receiveOptions, selectedReceiveCoinId]);

  useEffect(() => {
    if (!isCryptoPay) return;
    if (receiveOptions.length === 0) {
      if (selectedReceiveCoinId) setSelectedReceiveCoinId("");
      return;
    }
    if (receiveOptions.length === 1) {
      const onlyId = receiveOptions[0]?.coin?.id || "";
      if (selectedReceiveCoinId !== onlyId) setSelectedReceiveCoinId(onlyId);
      return;
    }
    const stillValid = receiveOptions.some(
      (row) => row.coin?.id === selectedReceiveCoinId,
    );
    if (!stillValid) {
      setSelectedReceiveCoinId(receiveOptions[0]?.coin?.id || "");
    }
  }, [
    isCryptoPay,
    receiveOptions,
    selectedReceiveCoinId,
    selectedExternalRow?.coin?.id,
  ]);

  const fromCoinId = isCryptoPay
    ? selectedExternalRow?.coin?.id
    : xrphCoin?.coin?.id;
  const toCoinId = isCryptoPay
    ? selectedReceiveRow?.coin?.id
    : selectedExternalRow?.coin?.id;

  const fromBalance = isCryptoPay
    ? balanceNumber(selectedExternalRow)
    : balanceNumber(xrphCoin);
  const toBalance = isCryptoPay
    ? balanceNumber(selectedReceiveRow)
    : balanceNumber(selectedExternalRow);

  const externalSymbol = (
    isConnected
      ? selectedExternalRow?.coin?.symbol || "XRP"
      : prefillFromSymbol || unconnectedPaySymbol || "XRP"
  ).toUpperCase();
  const receiveSymbol = (
    selectedReceiveRow?.coin?.symbol || ALLOWED_TO_SYMBOL
  ).toUpperCase();
  const fromSymbol = (isCryptoPay ? externalSymbol : "XRPH").toUpperCase();
  const toSymbol = (isCryptoPay ? receiveSymbol : externalSymbol).toUpperCase();

  const getUsdPrice = (symbol: string) => {
    if (!coinPrices) return 0;
    const upper = symbol.toUpperCase();
    const asObject = Array.isArray(coinPrices) ? {} : coinPrices;

    const byFiat = asObject.usd as Record<string, unknown> | undefined;
    if (byFiat && typeof byFiat[upper] === "number") {
      return Number(byFiat[upper]);
    }

    const byToken = asObject[upper] as Record<string, unknown> | undefined;
    if (byToken && typeof byToken.usd === "number") {
      return Number(byToken.usd);
    }

    const maybeArray = Array.isArray(coinPrices)
      ? coinPrices
      : Array.isArray((coinPrices as Record<string, unknown>).coins)
        ? ((coinPrices as Record<string, unknown>).coins as Array<
            Record<string, unknown>
          >)
        : [];
    const found = maybeArray.find((item) => {
      const coin = (item.coin as Record<string, unknown> | undefined) || {};
      const sym = String(coin.symbol || item.symbol || "").toUpperCase();
      return sym === upper;
    });
    if (found) {
      if (typeof found.usd === "number") return Number(found.usd);
      const prices = found.prices as Record<string, unknown> | undefined;
      if (prices && typeof prices.usd === "number") return Number(prices.usd);
    }

    return 0;
  };

  const getCoinEntry = (symbol: string) => {
    if (!coinPrices) return undefined;
    const upper = symbol.toUpperCase();
    const maybeArray = Array.isArray(coinPrices)
      ? coinPrices
      : Array.isArray((coinPrices as Record<string, unknown>).coins)
        ? ((coinPrices as Record<string, unknown>).coins as Array<
            Record<string, unknown>
          >)
        : [];
    return maybeArray.find((item) => {
      const coin = (item.coin as Record<string, unknown> | undefined) || {};
      const sym = String(coin.symbol || item.symbol || "").toUpperCase();
      return sym === upper;
    });
  };

  const parsedAmount = Number(amount || 0);
  const fromUsdPrice = getUsdPrice(fromSymbol);
  const toUsdPrice = getUsdPrice(toSymbol);
  const payUsdValue =
    parsedAmount > 0 && fromUsdPrice > 0 ? parsedAmount * fromUsdPrice : 0;

  const xrphEntry = getCoinEntry("XRPH");
  const xrphPrices =
    (xrphEntry?.prices as Record<string, unknown> | undefined) || {};
  const xrphPriceInXrp =
    typeof xrphPrices.xrp === "number" && Number(xrphPrices.xrp) > 0
      ? Number(xrphPrices.xrp)
      : 0;

  let estimatedReceiveAmount = 0;
  if (parsedAmount > 0) {
    // Prefer direct pair-rate for XRP <-> XRPH to match wallet-app behavior.
    if (fromSymbol === "XRP" && toSymbol === "XRPH" && xrphPriceInXrp > 0) {
      estimatedReceiveAmount = parsedAmount / xrphPriceInXrp;
    } else if (
      fromSymbol === "XRPH" &&
      toSymbol === "XRP" &&
      xrphPriceInXrp > 0
    ) {
      estimatedReceiveAmount = parsedAmount * xrphPriceInXrp;
    } else if (fromUsdPrice > 0 && toUsdPrice > 0) {
      estimatedReceiveAmount = (parsedAmount * fromUsdPrice) / toUsdPrice;
    }
  }
  const receiveUsdValue = estimatedReceiveAmount * toUsdPrice;

  const loadBalances = async (address: string) => {
    if (!API_BASE_URL || !address) {
      return;
    }

    const response = await apiFetch(
      `${API_BASE_URL}/swap/getBalances/${encodeURIComponent(address)}`,
    );
    if (!response.ok) {
      throw new Error("Unable to load wallet balances.");
    }

    const data = (await response.json()) as BalancesResponse;
    const allRows = (data.balances || []).filter((row) => {
      const coin = row.coin;
      return !!coin?.id && coin?.isActive !== false;
    });

    const pairTargetIds = new Set<string>();
    for (const row of allRows) {
      for (const pid of row.coin?.swapPairs || []) {
        if (typeof pid === "string" && pid) pairTargetIds.add(pid);
      }
    }

    const withPairs = allRows.filter(
      (row) => (row.coin?.swapPairs || []).length > 0,
    );
    const xrphRow = allRows.find(
      (row) => (row.coin?.symbol || "").toUpperCase() === "XRPH",
    );
    const receiveOnlyRows = allRows.filter((row) => {
      const id = row.coin?.id;
      if (!id) return false;
      if ((row.coin?.swapPairs || []).length > 0) return false;
      return pairTargetIds.has(id);
    });

    const ordered: BalanceItem[] = [];
    const seen = new Set<string>();
    const pushRow = (row?: BalanceItem) => {
      const id = row?.coin?.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      if (row) ordered.push(row);
    };

    pushRow(xrphRow);
    for (const row of withPairs) pushRow(row);
    for (const row of receiveOnlyRows) pushRow(row);

    setBalanceRows(ordered);
  };

  const loadCoinPrices = async () => {
    if (!API_BASE_URL) return;
    const response = await apiFetch(`${API_BASE_URL}/swap/getCoinPrices`);
    if (response.ok) {
      const data = (await response.json()) as CoinPricesResponse;
      setCoinPrices(data);
      return;
    }

    const fallbackResponse = await apiFetch(`${API_BASE_URL}/swap/getPrices`);
    if (!fallbackResponse.ok) {
      throw new Error("Unable to load coin prices.");
    }
    const fallbackData = (await fallbackResponse.json()) as CoinPricesResponse;
    setCoinPrices(fallbackData);
  };

  const probeGetCoinPrices = async () => {
    if (!API_BASE_URL) return;
    const response = await apiFetch(`${API_BASE_URL}/swap/getCoinPrices`);
    const data = await response.json();
    // Debug-only probe requested: call endpoint on connect and expose raw payload.
    // eslint-disable-next-line no-console
    console.log("getCoinPrices response:", data);
  };

  const handleWalletConnected = async (payload: WalletLoginPayload) => {
    if (payload?.status === "Login Success" && payload?.token) {
      clearOpenInWalletHintModalTimer();
      setSwapOpenWalletHintOpen(false);
      setWalletForegroundHintVariant("swap");

      balanceBootstrapHandledRef.current = true;
      const nextWalletAddress = payload?.user?.wallet_address || "";
      const nextChannelHash =
        payload?.user?.channel_data_hash || channelDataHash;

      setWalletAddress(nextWalletAddress);
      setConnectToken(payload.token);
      setChannelDataHash(nextChannelHash);
      setIsConnected(true);
      if (nextWalletAddress && nextChannelHash) {
        writeSwapSession({
          token: payload.token,
          channel_data_hash: nextChannelHash,
          wallet_address: nextWalletAddress,
        });
      }
      onWalletConnected?.(nextWalletAddress);
      setIsConnect(false);
      setIsConnecting(false);

      try {
        await loadBalances(nextWalletAddress);
        await probeGetCoinPrices();
        await loadCoinPrices();
      } catch (error) {
        toast({
          status: "error",
          title: "Balance load failed",
          description:
            error instanceof Error
              ? error.message
              : "Could not load wallet balances.",
        });
      }

      toast({
        status: "success",
        title: "Wallet connected",
        description: nextWalletAddress
          ? `Connected: ${nextWalletAddress}`
          : "Login approved from mobile wallet.",
      });
      return;
    }

    toast({
      status: "error",
      title: "Wallet connect failed",
      description: "Received an unexpected approval response.",
    });
    setIsConnecting(false);
  };

  const startWalletConnect = async () => {
    if (!API_BASE_URL) {
      toast({
        status: "error",
        title: "Missing API URL",
        description: "Set REACT_APP_API_BASE_URL in your environment.",
      });
      return;
    }

    if (!socketRef.current) {
      toast({
        status: "error",
        title: "Socket unavailable",
        description: "Unable to initialize swap socket connection.",
      });
      return;
    }

    try {
      setIsConnecting(true);
      setIsConnect(true);
      setIsConnected(false);
      setChannelDataHash("");

      const response = await apiFetch(`${API_BASE_URL}/swap/getQRCode`);
      if (!response.ok) {
        throw new Error("Could not fetch QR code payload.");
      }

      const data = (await response.json()) as QrCodeResponse;
      if (!data?.channel_data_hash) {
        throw new Error("Invalid QR response shape.");
      }

      setChannelDataHash(data.channel_data_hash);

      socketRef.current.off(data.channel_data_hash);
      socketRef.current.on(data.channel_data_hash, handleWalletConnected);
    } catch (error) {
      setIsConnecting(false);
      toast({
        status: "error",
        title: "Connect flow failed",
        description:
          error instanceof Error
            ? error.message
            : "Unable to start wallet connect flow.",
      });
    }
  };

  const handleSwap = async () => {
    const parsedAmount = Number(amount);

    if (
      !socketRef.current ||
      !connectToken ||
      !walletAddress ||
      !channelDataHash
    ) {
      toast({
        status: "error",
        title: "Missing swap session",
        description: "Reconnect wallet before swapping.",
      });
      return;
    }

    if (!fromCoinId || !toCoinId || !parsedAmount || parsedAmount <= 0) {
      toast({
        status: "error",
        title: "Invalid swap details",
        description: "Select coins and enter a valid amount.",
      });
      return;
    }

    if (
      !ALLOWED_FROM_SYMBOLS.has(fromSymbol) ||
      toSymbol !== ALLOWED_TO_SYMBOL
    ) {
      toast({
        status: "error",
        title: "Pair not supported",
        description:
          "Only XRP -> XRPHAI and RLUSD -> XRPHAI swaps are allowed right now.",
      });
      return;
    }

    if (fromSymbol === "XRP") {
      const xrpBalance = balanceNumber(selectedExternalRow);
      const maxSwappableXrp = Math.max(0, xrpBalance - 1.8);
      if (parsedAmount > maxSwappableXrp) {
        toast({
          status: "error",
          title: "Insufficient swappable XRP",
          description:
            maxSwappableXrp > 0
              ? `You can swap up to ${maxSwappableXrp.toFixed(
                  6,
                )} XRP after the 1.8 XRP reserve.`
              : "You need more XRP above the 1.8 XRP reserve to swap.",
        });
        return;
      }
    }

    if (swapInFlightRef.current || isSwapLoading) {
      return;
    }
    swapInFlightRef.current = true;

    const id = createClientSwapId();

    const tryFinalizePendingSwap = (expectId: string): boolean => {
      const g = pendingSwapGateRef.current;
      if (!g || g.id !== expectId) return false;
      pendingSwapGateRef.current = null;
      return true;
    };

    const detachSwapSocketSubscriptions = () => {
      const hx = swapSocketHandlersRef.current;
      const s = socketRef.current;
      if (!hx || !s) return;
      const hid = hx.id;
      s.off(`swap-request-${hid}`, hx.onSwapResult);
      s.off(`swap-request-error-${hid}`, hx.onSwapRequestError);
      s.off(`swap-response-error-${hid}`, hx.onSwapResponseError);
      swapSocketHandlersRef.current = null;
    };

    try {
      setIsSwapLoading(true);

      // Build dynamic link/transaction context as enabled by backend flow.
      const dynamicLinkResponse = await apiFetch(
        `${API_BASE_URL}/swap/getSwapDynamicLink`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            channel_data_hash: channelDataHash,
            fromCoin: fromCoinId,
            toCoin: toCoinId,
            amount: parsedAmount,
            userId: walletAddress,
            token: connectToken,
          }),
        },
      );
      if (!dynamicLinkResponse.ok) {
        let backendMessage = "Unable to build swap dynamic link.";
        try {
          const rawError = await dynamicLinkResponse.text();
          if (rawError) {
            try {
              const errorData = JSON.parse(rawError) as {
                message?: string;
                error?: string;
              };
              backendMessage =
                errorData?.message || errorData?.error || rawError;
            } catch {
              backendMessage = rawError;
            }
          }
        } catch {
          // no-op
        }
        throw new Error(backendMessage);
      }

      pendingSwapGateRef.current = { id };

      const onSwapResult = async (payload?: {
        status?: string;
        message?: string;
        error?: string;
      }) => {
        if (!tryFinalizePendingSwap(id)) return;
        detachSwapSocketSubscriptions();
        clearPendingSwapHintModalTimer();
        clearOpenInWalletHintModalTimer();
        setSwapOpenWalletHintOpen(false);
        swapInFlightRef.current = false;
        setIsSwapLoading(false);

        const resultStatus = String(payload?.status || "").toLowerCase();

        if (resultStatus === "approved") {
          setSuccessRewardsModalOpen(true);
          setAmount("");
          setPrefillFromSymbol("");
          clearSwapPrefill();
          // Wallet balances can lag on-chain/indexer updates; refresh twice after delay.
          setTimeout(() => {
            loadBalances(walletAddress).catch(() => {
              // no-op
            });
          }, 5000);
          setTimeout(() => {
            loadBalances(walletAddress).catch(() => {
              // no-op
            });
          }, 10000);
          return;
        }

        if (resultStatus === "rejected") {
          toast({
            status: "error",
            title: "Swap rejected",
            description:
              payload?.message ||
              payload?.error ||
              "Swap request was rejected on mobile.",
          });
          return;
        }

        toast({
          status: "error",
          title: "Swap failed",
          description:
            payload?.message ||
            payload?.error ||
            "Swap status was not recognized.",
        });
      };

      const onSwapRequestError = (error: {
        message?: string;
        error?: string;
      }) => {
        if (!tryFinalizePendingSwap(id)) return;
        detachSwapSocketSubscriptions();
        clearPendingSwapHintModalTimer();
        clearOpenInWalletHintModalTimer();
        setSwapOpenWalletHintOpen(false);
        swapInFlightRef.current = false;
        setIsSwapLoading(false);
        toast({
          status: "error",
          title: "Swap request failed",
          description:
            error?.message ||
            error?.error ||
            "Swap request could not be sent.",
        });
      };

      const onSwapResponseError = (error: {
        message?: string;
        error?: string;
      }) => {
        if (!tryFinalizePendingSwap(id)) return;
        detachSwapSocketSubscriptions();
        clearPendingSwapHintModalTimer();
        clearOpenInWalletHintModalTimer();
        setSwapOpenWalletHintOpen(false);
        swapInFlightRef.current = false;
        setIsSwapLoading(false);
        toast({
          status: "error",
          title: "Swap failed",
          description:
            error?.message ||
            error?.error ||
            "Mobile approval was rejected or failed.",
        });
      };

      swapSocketHandlersRef.current = {
        id,
        onSwapResult,
        onSwapRequestError,
        onSwapResponseError,
      };

      socketRef.current.off(`swap-request-${id}`, onSwapResult);
      socketRef.current.off(`swap-request-error-${id}`, onSwapRequestError);
      socketRef.current.off(`swap-response-error-${id}`, onSwapResponseError);
      socketRef.current.on(`swap-request-${id}`, onSwapResult);
      socketRef.current.on(`swap-request-error-${id}`, onSwapRequestError);
      socketRef.current.on(`swap-response-error-${id}`, onSwapResponseError);

      socketRef.current.emit("swap-request", {
        id,
        fromCoin: fromCoinId,
        toCoin: toCoinId,
        amount: parsedAmount,
        token: connectToken,
      });

      reattachPendingSwapSocketSubscriptions();

      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "")) {
        clearPendingSwapHintModalTimer();
        clearOpenInWalletHintModalTimer();
        window.setTimeout(() => {
          openXrphWalletAppForegroundFromSwapMobileBrowser();
        }, SWAP_MOBILE_APP_OPEN_DELAY_MS);
        swapHintShowTimerRef.current = window.setTimeout(() => {
          swapHintShowTimerRef.current = null;
          setWalletForegroundHintVariant("swap");
          setSwapOpenWalletHintOpen(true);
        }, SWAP_MOBILE_MANUAL_HINT_MODAL_DELAY_MS);
      }
    } catch (error) {
      pendingSwapGateRef.current = null;
      detachSwapSocketSubscriptions();
      clearPendingSwapHintModalTimer();
      clearOpenInWalletHintModalTimer();
      setSwapOpenWalletHintOpen(false);
      swapInFlightRef.current = false;
      setIsSwapLoading(false);
      toast({
        status: "error",
        title: "Swap setup failed",
        description:
          error instanceof Error ? error.message : "Unable to start swap.",
      });
    }
  };

  const handleOpenInWallet = () => {
    if (!channelDataHash) {
      return;
    }

    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile) {
      window.open(XRPH_WALLET_APP_URL, "_blank", "noopener,noreferrer");
      return;
    }

    clearPendingSwapHintModalTimer();
    clearOpenInWalletHintModalTimer();
    openInWalletHintTimerRef.current = window.setTimeout(() => {
      openInWalletHintTimerRef.current = null;
      setWalletForegroundHintVariant("connection");
      setSwapOpenWalletHintOpen(true);
    }, SWAP_MOBILE_MANUAL_HINT_MODAL_DELAY_MS);

    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isIOS) {
      // Use the wallet's Universal Link directly.
      // If the app is installed, iOS opens it and passes this URL to the app.
      // If not installed, the web fallback page handles showing App Store install.
      const universalPaymentUrl = `${XRPH_WALLET_IOS_PAYMENT_URL_BASE}?channel_data_hash=${encodeURIComponent(
        channelDataHash,
      )}`;
      window.location.assign(universalPaymentUrl);
      return;
    }

    if (isAndroid) {
      const encodedHash = encodeURIComponent(channelDataHash);
      const androidIntentUrl = `intent://paymentrequest?channel_data_hash=${encodedHash}#Intent;scheme=com.xrphealthcare.xrphwallet;package=com.xrphwallet;end`;
      window.location.assign(androidIntentUrl);
      return;
    }

    // Other mobile browsers: fall back to the public install page.
    window.location.assign(XRPH_WALLET_APP_URL);
  };

  useEffect(() => {
    return () => {
      if (swapHintShowTimerRef.current !== null) {
        window.clearTimeout(swapHintShowTimerRef.current);
        swapHintShowTimerRef.current = null;
      }
      if (openInWalletHintTimerRef.current !== null) {
        window.clearTimeout(openInWalletHintTimerRef.current);
        openInWalletHintTimerRef.current = null;
      }
      cleanupSocket();
    };
  }, []);

  useEffect(() => {
    if (connectRequestNonce > 0) {
      startWalletConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectRequestNonce]);

  useEffect(() => {
    // Enable estimates even before wallet connect.
    loadCoinPrices().catch(() => {
      // no-op
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadCoinPrices is stable enough for this
  }, []);

  useEffect(() => {
    if (isConnected) {
      loadCoinPrices().catch(() => {
        // Keep UI usable even if estimate prices fail.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useOutsideClick({
    ref: dropRef,
    handler: () => setIsDropdown(false),
  });

  useOutsideClick({
    ref: receiveDropRef,
    handler: () => setIsReceiveDropdown(false),
  });

  useOutsideClick({
    ref: inputRef,
    handler: () => setIsInputActive(false),
  });

  return (
    <Box
      p="24px"
      bg="#202226"
      border="0.4px solid #36394A"
      borderRadius="12px"
      maxW="511px"
      w="100%"
      mb="24px"
    >
      {isConnect ? (
        <>
          <Flex align={"center"} gap="16px">
            <IconChevronLeft
              color="#fff"
              size="28px"
              cursor="pointer"
              onClick={() => {
                clearOpenInWalletHintModalTimer();
                setSwapOpenWalletHintOpen(false);
                setWalletForegroundHintVariant("swap");
                setIsConnect(false);
                setIsConnecting(false);
              }}
            />
            <Flex align="center" justify="space-between" flex="1">
              <Text color="#fff" fontSize="24px" fontWeight="700">
                Scan to Connect Wallet
              </Text>
              <Flex
                height="32px"
                width="32px"
                borderRadius="16px"
                bg="#1A1A1A"
                cursor="pointer"
                justify="center"
                align="center"
                onClick={() => setConnectHelpOpen(true)}
                aria-label="How to connect wallet"
              >
                <IconInfoCircle color="#727997" size="18px" />
              </Flex>
            </Flex>
          </Flex>
          <Text
            mt="54px"
            textAlign="center"
            color="#727997"
            fontSize="14px"
            fontWeight="400"
          >
            Security QR Code
          </Text>
          <Flex
            mx="auto"
            mt="20px"
            width="230px"
            height="230px"
            borderRadius="12px"
            p="1px"
            bg="linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
          >
            <Flex
              justify="center"
              align="center"
              bg="#181818"
              width="100%"
              height="100%"
              borderRadius="12px"
            >
              <Flex
                height="100%"
                width="100%"
                justify="center"
                align="center"
                bg=" linear-gradient(90deg, rgba(3, 249, 130, 0.05) -7.95%, rgba(55, 195, 166, 0.05) 20.29%, rgba(133, 114, 219, 0.05) 61.19%, rgba(175, 69, 238, 0.05) 115.47%)"
              >
                {channelDataHash ? (
                  <QRCode
                    value={channelDataHash}
                    bgColor="transparent"
                    fgColor="#fff"
                    ecLevel="H"
                  />
                ) : (
                  <Flex direction="column" align="center" gap="10px">
                    <Spinner color="#fff" thickness="3px" size="xl" />
                    <Text color="#fff" fontSize="12px" fontWeight="500">
                      Loading QR...
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
          <Text
            maxW="305px"
            mx="auto"
            mt="20px"
            color="#727997"
            textAlign="center"
            fontSize="14px"
            fontWeight="400"
          >
            {isConnecting
              ? "Scan this QR code on your mobile wallet to approve login."
              : "Waiting for wallet approval confirmation."}
          </Text>
          <Box
            mt="24px"
            bg="linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
            p="1px"
            borderRadius="12px"
          >
            <Box bg="#181818" borderRadius="12px">
              <Button
                w="100%"
                p="27px 18px"
                borderRadius="12px"
                bg="linear-gradient(90deg, rgba(3, 249, 130, 0.10) -7.95%, rgba(55, 195, 166, 0.10) 20.29%, rgba(133, 114, 219, 0.10) 61.19%, rgba(175, 69, 238, 0.10) 115.47%)"
                _hover={{
                  bg: "linear-gradient(90deg, rgba(3, 249, 130, 0.10) -7.95%, rgba(55, 195, 166, 0.10) 20.29%, rgba(133, 114, 219, 0.10) 61.19%, rgba(175, 69, 238, 0.10) 115.47%)",
                }}
                color="#fff"
                fontWeight="500"
                fontSize="18px"
                onClick={handleOpenInWallet}
                isDisabled={!channelDataHash}
              >
                Open In XRPH Wallet
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Text
            fontSize="24px"
            fontWeight="700"
            color="#fff"
            textAlign="center"
          >
            Swap XRP or RLUSD to XRPHAI
          </Text>
          <Text
            fontSize="14px"
            fontWeight="400"
            color="rgba(204, 204, 204, 0.70)"
            textAlign="center"
          >
            Swap instantly at live market rates
          </Text>
          <Flex flexDir="column" gap="20px">
            <Box
              mt="24px"
              borderRadius="8px"
              p="1px"
              bg={
                isInputActive
                  ? "linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
                  : ""
              }
              boxShadow={
                isInputActive
                  ? "0px 0px 44px 0px rgba(55, 195, 166, 0.15)"
                  : "none"
              }
              ref={inputRef}
            >
              <Box bg="#1A1A1A" borderRadius={"8px"} p="18px 10px">
                <Flex align="center" justify="space-between">
                  <Text fontSize={"12px"} color="#ccc" fontWeight="400">
                    You Pay
                  </Text>
                  <Text fontSize={"12px"} color="#ccc" fontWeight="300">
                    ${payUsdValue.toFixed(2)}
                  </Text>
                </Flex>
                <Flex align="center" justify="space-between" mt="12px">
                  <Input
                    ref={amountInputRef}
                    maxW="50%"
                    bg="transparent"
                    border="none"
                    outline="none"
                    _focusVisible={{ border: "none" }}
                    placeholder="0"
                    type="text"
                    inputMode="decimal"
                    pattern="^[0-9]*[.]?[0-9]*$"
                    px="0"
                    color="#fff"
                    fontSize="24px"
                    fontWeight="500"
                    value={amount}
                    onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
                    onClick={() => setIsInputActive(true)}
                  />
                  {isCryptoPay ? (
                    <Flex
                      align="center"
                      bg="#1d1d1d"
                      borderRadius="50px"
                      gap="8px"
                      p="4px 8px 4px 6px"
                      cursor="pointer"
                      position="relative"
                      onClick={() => {
                        setIsReceiveDropdown(false);
                        setIsDropdown(!isDropdown);
                      }}
                    >
                      <Image
                        src={tokenImage(externalSymbol)}
                        alt="coin"
                        {...TOKEN_ICON_BOX}
                      />
                      <Text fontSize="14px" fontWeight="500" color="#fff">
                        {externalSymbol}
                      </Text>
                      <IconChevronDown size="20px" color="#fff" />
                      {isDropdown && (
                        <Flex
                          pos="absolute"
                          p="12px"
                          bg="#202226"
                          border="0.5px solid #36394A"
                          borderRadius="8px"
                          w="111px"
                          flexDir="column"
                          gap="12px"
                          top="0"
                          right="0"
                          marginTop="45px"
                          marginRight="5px"
                          ref={dropRef}
                        >
                          {(isConnected
                            ? availableExternalRows.map((row) => ({
                                key: row.coin?.id || (row.coin?.symbol || ""),
                                symbol: (row.coin?.symbol || "").toUpperCase(),
                                onPick: () => {
                                  setSelectedExternalCoinId(row.coin?.id || "");
                                  setIsDropdown(false);
                                },
                              }))
                            : ["XRP", "RLUSD"].map((sym) => ({
                                key: sym,
                                symbol: sym,
                                onPick: () => {
                                  setUnconnectedPaySymbol(sym);
                                  setPrefillFromSymbol(sym);
                                  setIsDropdown(false);
                                },
                              }))
                          ).map((opt) => (
                            <Flex
                              key={opt.key}
                              gap="8px"
                              align="center"
                              cursor="pointer"
                              onClick={opt.onPick}
                            >
                              <Image
                                src={tokenImage(opt.symbol)}
                                alt={opt.symbol}
                                {...TOKEN_ICON_BOX}
                              />
                              <Text fontSize="14px" fontWeight="500" color="#fff">
                                {opt.symbol}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      )}
                    </Flex>
                  ) : (
                    <Flex
                      align="center"
                      bg="#1d1d1d"
                      borderRadius="50px"
                      gap="8px"
                      p="4px 8px 4px 6px"
                      cursor="default"
                    >
                      <Image
                        src={tokenImage(externalSymbol)}
                        alt="coin"
                        {...TOKEN_ICON_BOX}
                      />
                      <Text fontSize="14px" fontWeight="500" color="#fff">
                        {externalSymbol}
                      </Text>
                    </Flex>
                  )}
                </Flex>
                {isConnected && (
                  <Flex align="center" justify="flex-end" mt="12px">
                    <Text fontSize={"14px"} color="#ccc" fontWeight="300">
                      Balance: {fromBalance}
                    </Text>
                  </Flex>
                )}
                {fromSymbol === "XRP" && (
                  <Text
                    mt="6px"
                    fontSize="11px"
                    color="#727997"
                    fontWeight="400"
                    textAlign="right"
                  >
                    Includes 1.8 XRP reserve that isn't swappable.
                  </Text>
                )}
              </Box>
            </Box>
            <Flex justify="center">
              {/* Inverse disabled: only XRP/USDT/RLUSD → XRPH/XRPHAI is supported */}
              <IconArrowsUpDown
                size="24px"
                color="#fff"
                cursor="default"
                aria-hidden
              />
            </Flex>
            <Box bg="#1A1A1A" borderRadius={"8px"} p="18px 10px">
              <Flex align="center" justify="space-between">
                <Text fontSize={"12px"} color="#ccc" fontWeight="400">
                  You Receive
                </Text>
                <Text fontSize={"12px"} color="#ccc" fontWeight="300">
                  ${receiveUsdValue.toFixed(2)}
                </Text>
              </Flex>
              <Flex align="center" justify="space-between" mt="12px">
                <Input
                  maxW="50%"
                  bg="transparent"
                  border="none"
                  outline="none"
                  _focusVisible={{ border: "none" }}
                  placeholder="0"
                  px="0"
                  color="#fff"
                  fontSize="24px"
                  fontWeight="500"
                  value={
                    estimatedReceiveAmount > 0
                      ? estimatedReceiveAmount.toFixed(6)
                      : "0"
                  }
                  readOnly
                />
                {!isCryptoPay ? (
                  <Flex
                    align="center"
                    bg="#1d1d1d"
                    borderRadius="50px"
                    gap="8px"
                    p="4px 8px 4px 6px"
                    cursor="pointer"
                    position="relative"
                    onClick={() => {
                      setIsReceiveDropdown(false);
                      setIsDropdown(!isDropdown);
                    }}
                  >
                    <Image
                      src={tokenImage(externalSymbol)}
                      alt="coin"
                      {...TOKEN_ICON_BOX}
                    />
                    <Text fontSize="14px" fontWeight="500" color="#fff">
                      {externalSymbol}
                    </Text>
                    <IconChevronDown size="20px" color="#fff" />
                    {isDropdown && (
                      <Flex
                        pos="absolute"
                        p="12px"
                        bg="#202226"
                        border="0.5px solid #36394A"
                        borderRadius="8px"
                        w="111px"
                        flexDir="column"
                        gap="12px"
                        top="0"
                        right="0"
                        marginTop="45px"
                        marginRight="5px"
                        ref={dropRef}
                      >
                        {availableExternalRows.map((row) => {
                          const symbol = (row.coin?.symbol || "").toUpperCase();
                          return (
                            <Flex
                              key={row.coin?.id || symbol}
                              gap="8px"
                              align="center"
                              cursor="pointer"
                              onClick={() => {
                                setSelectedExternalCoinId(row.coin?.id || "");
                                setIsDropdown(false);
                              }}
                            >
                              <Image
                                src={tokenImage(symbol)}
                                alt={symbol}
                                {...TOKEN_ICON_BOX}
                              />
                              <Text
                                fontSize="14px"
                                fontWeight="500"
                                color="#fff"
                              >
                                {symbol}
                              </Text>
                            </Flex>
                          );
                        })}
                      </Flex>
                    )}
                  </Flex>
                ) : receiveOptions.length > 1 ? (
                  <Flex
                    align="center"
                    bg="#1d1d1d"
                    borderRadius="50px"
                    gap="8px"
                    p="4px 8px 4px 6px"
                    cursor="pointer"
                    position="relative"
                    onClick={() => {
                      setIsDropdown(false);
                      setIsReceiveDropdown(!isReceiveDropdown);
                    }}
                  >
                    <Image
                      src={tokenImage(receiveSymbol)}
                      alt="receive"
                      {...TOKEN_ICON_BOX}
                    />
                    <Text fontSize="14px" fontWeight="500" color="#fff">
                      {receiveSymbol}
                    </Text>
                    <IconChevronDown size="20px" color="#fff" />
                    {isReceiveDropdown && (
                      <Flex
                        pos="absolute"
                        p="12px"
                        bg="#202226"
                        border="0.5px solid #36394A"
                        borderRadius="8px"
                        w="111px"
                        flexDir="column"
                        gap="12px"
                        top="0"
                        right="0"
                        marginTop="45px"
                        marginRight="5px"
                        ref={receiveDropRef}
                        zIndex={2}
                      >
                        {receiveOptions.map((row) => {
                          const symbol = (row.coin?.symbol || "").toUpperCase();
                          return (
                            <Flex
                              key={row.coin?.id || symbol}
                              gap="8px"
                              align="center"
                              cursor="pointer"
                              onClick={() => {
                                setSelectedReceiveCoinId(row.coin?.id || "");
                                setIsReceiveDropdown(false);
                              }}
                            >
                              <Image
                                src={tokenImage(symbol)}
                                alt={symbol}
                                {...TOKEN_ICON_BOX}
                              />
                              <Text
                                fontSize="14px"
                                fontWeight="500"
                                color="#fff"
                              >
                                {symbol}
                              </Text>
                            </Flex>
                          );
                        })}
                      </Flex>
                    )}
                  </Flex>
                ) : (
                  <Flex
                    align="center"
                    bg="#1d1d1d"
                    borderRadius="50px"
                    gap="8px"
                    p="4px 8px 4px 6px"
                    cursor="default"
                  >
                    <Image
                      src={tokenImage(receiveSymbol)}
                      alt="receive"
                      {...TOKEN_ICON_BOX}
                    />
                    <Text fontSize="14px" fontWeight="500" color="#fff">
                      {receiveSymbol}
                    </Text>
                  </Flex>
                )}
              </Flex>
              {isConnected && (
                <Flex align="center" justify="flex-end" mt="12px">
                  <Text fontSize={"14px"} color="#ccc" fontWeight="300">
                    Balance: {toBalance}
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>
          {isConnected ? (
            <Button
              mt="24px"
              w="100%"
              p="27px 18px"
              borderRadius="12px"
              border="none"
              bg="linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
              _hover={{
                bg: "linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)",
              }}
              color="#fff"
              fontWeight="500"
              fontSize="18px"
              onClick={handleSwap}
              isLoading={isSwapLoading}
            >
              Swap
            </Button>
          ) : (
            <Box
              mt="24px"
              bg="linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
              p="1px"
              borderRadius="12px"
            >
              <Box bg="#181818" borderRadius="12px">
                <Button
                  w="100%"
                  p="27px 18px"
                  borderRadius="12px"
                  bg="linear-gradient(90deg, rgba(3, 249, 130, 0.10) -7.95%, rgba(55, 195, 166, 0.10) 20.29%, rgba(133, 114, 219, 0.10) 61.19%, rgba(175, 69, 238, 0.10) 115.47%)"
                  _hover={{
                    bg: "linear-gradient(90deg, rgba(3, 249, 130, 0.10) -7.95%, rgba(55, 195, 166, 0.10) 20.29%, rgba(133, 114, 219, 0.10) 61.19%, rgba(175, 69, 238, 0.10) 115.47%)",
                  }}
                  color="#fff"
                  fontWeight="500"
                  fontSize="18px"
                  onClick={startWalletConnect}
                >
                  Connect Wallet
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      <Modal isOpen={connectHelpOpen} onClose={() => setConnectHelpOpen(false)} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent
          bg="#202226"
          border="0.5px solid #36394A"
          borderRadius="14px"
          maxW="420px"
          mx="14px"
        >
          <ModalCloseButton color="#727997" zIndex={1} />
          <ModalBody pt="22px" px="20px" pb="14px">
            <Text color="#fff" fontSize="18px" fontWeight="700" mb="10px">
              How to connect your wallet
            </Text>
            <Flex direction="column" gap="10px">
              <Text color="#C9D0E6" fontSize="13px" lineHeight="1.55">
                1) Open the <strong>XRPH Wallet</strong> app.
              </Text>
              <Text color="#C9D0E6" fontSize="13px" lineHeight="1.55">
                2) Open XRPH app QR scanner:
                <br />
                - Tap the <strong>Scan</strong>{" "}
                <Image
                  src={ObjectScanSvg}
                  alt="Scan"
                  height="14px"
                  width="14px"
                  display="inline-block"
                  verticalAlign="text-bottom"
                  mx="2px"
                />{" "}
                icon on the home screen, or
                <br />
                - Go to <strong>Settings</strong> → <strong>Wallet Connect</strong> →
                  <strong> New Connection</strong>.
              </Text>
              <Text color="#C9D0E6" fontSize="13px" lineHeight="1.55">
                3) Scan the QR code shown on this page.
              </Text>
              <Text color="#C9D0E6" fontSize="13px" lineHeight="1.55">
                4) Approve the confirmation prompt in the app to finish connecting.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter pt="0" pb="18px" px="20px">
            <Button
              w="100%"
              borderRadius="12px"
              bg="linear-gradient(90deg, rgba(3, 249, 130, 0.12) -7.95%, rgba(55, 195, 166, 0.12) 20.29%, rgba(133, 114, 219, 0.12) 61.19%, rgba(175, 69, 238, 0.12) 115.47%)"
              _hover={{
                bg: "linear-gradient(90deg, rgba(3, 249, 130, 0.12) -7.95%, rgba(55, 195, 166, 0.12) 20.29%, rgba(133, 114, 219, 0.12) 61.19%, rgba(175, 69, 238, 0.12) 115.47%)",
              }}
              color="#fff"
              fontWeight="600"
              onClick={() => setConnectHelpOpen(false)}
            >
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={swapOpenWalletHintOpen}
        onClose={() => {
          setSwapOpenWalletHintOpen(false);
          setWalletForegroundHintVariant("swap");
        }}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent
          bg="#202226"
          border="0.5px solid #36394A"
          borderRadius="14px"
          maxW="420px"
          mx="14px"
        >
          <ModalCloseButton color="#727997" zIndex={1} aria-label="Close" />
          <ModalBody pt="24px" pl="20px" pr="52px" pb="24px">
            <Text color="#C9D0E6" fontSize="15px" lineHeight="1.55">
              {walletForegroundHintVariant === "connection"
                ? "If the app doesn't open automatically, please open XRPH Wallet manually to confirm your connection."
                : "If the app doesn't open automatically, please open XRPH Wallet manually to confirm your swap."}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={successRewardsModalOpen}
        onClose={() => setSuccessRewardsModalOpen(false)}
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent
          bg="#202226"
          border="0.4px solid #36394A"
          borderRadius="12px"
          mx="16px"
          py="8px"
        >
          <ModalCloseButton color="#727997" zIndex={1} />
          <ModalBody pt="32px" px="24px" pb="8px">
            <Flex
              direction="column"
              align="center"
              textAlign="center"
              gap="20px"
            >
              <Flex
                w="92px"
                h="92px"
                borderRadius="full"
                align="center"
                justify="center"
                p="3px"
                bg="linear-gradient(135deg, #03F982 0%, #37C3A6 35%, #8572DB 70%, #AF45EE 100%)"
                boxShadow="0 0 32px rgba(3, 249, 130, 0.18)"
              >
                <Flex
                  w="100%"
                  h="100%"
                  borderRadius="full"
                  bg="#181818"
                  align="center"
                  justify="center"
                >
                  <Image
                    src={Logo}
                    alt="XRPHAI"
                    boxSize="52px"
                    objectFit="contain"
                  />
                </Flex>
              </Flex>
              <Box>
                <Text
                  as="h2"
                  color="#fff"
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="1.3"
                  mb="10px"
                >
                  XRPHAI received successfully.
                </Text>
                <Text
                  color="rgba(204, 204, 204, 0.88)"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="1.55"
                >
                  Hold XRPHAI to unlock enhanced reward boosts through the XRPH
                  AI ecosystem.
                </Text>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter flexDir="column" gap="3" pt="2" pb="6">
            <Button
              as="a"
              href={XRPHAI_REWARDS_URL}
              target="_blank"
              rel="noopener noreferrer"
              w="100%"
              p="22px 18px"
              borderRadius="12px"
              border="none"
              bg="linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)"
              _hover={{
                bg: "linear-gradient(90deg, #03F982 -7.95%, #37C3A6 20.29%, #8572DB 61.19%, #AF45EE 115.47%)",
              }}
              color="#fff"
              fontWeight="600"
              fontSize="16px"
            >
              How Rewards Work
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SwapCard;
